import { isStringValue } from '../lib/validation/primitives';
/**
 * Locale registry — the single source of truth for which languages exist and,
 * separately, which ones search engines are allowed to see.
 *
 * The two lists are deliberately distinct. See project_docs/specs/I18N_PLAN.md §2:
 * GSC (90d, 2026-08-02) shows 92.3% of impressions are USA+GBR with no measurable
 * non-English demand, and the H2 war plan pre-commits to "no indexed locale URLs
 * in 2026". So we ship a localised interface without adding a single URL to the
 * index. Promotion of a locale to INDEXED is a per-locale decision against the
 * evidence bar in the plan (§9), not a side effect of adding a translation.
 */

/** Every locale the interface is offered in. Order drives the language switcher. */
// SAFETY: The surrounding typed contract establishes the asserted representation.
export const SUPPORTED_LOCALES = [
  'en',
  'es',
  'fr',
  'de',
  'pt',
  'it',
  'ja',
  'ko',
  'hi',
  'ar',
  'ru',
  'nl',
] as const;

export type Locale = (typeof SUPPORTED_LOCALES)[number];

/**
 * The locale the catalogs are authored in. `messages/en.json` is generated from
 * marketing-copy.ts + ui-copy.ts and every other catalog is a translation of it.
 *
 * Deliberately separate from DEFAULT_LOCALE: "the language we write in" and "the
 * language an unrouted request gets" are different questions that happen to have
 * the same answer today. Conflating them meant the message loader short-circuited
 * to English whenever a locale equalled the default, so pointing DEFAULT_LOCALE at
 * another language served English copy under that language's `lang` attribute.
 */
export const SOURCE_LOCALE: Locale = 'en';

/** Locale served when a request carries no locale of its own. */
export const DEFAULT_LOCALE: Locale = 'en';

/**
 * Locales search engines may index.
 *
 * ⚠️ Adding a locale here is an SEO decision, not a translation decision. It puts
 * ~20 URLs into the sitemap, hreflang and IndexNow. Do not extend this list
 * without the four-part evidence bar in I18N_PLAN.md §9 (rebuild experiment
 * resolved · disavow clean · Ahrefs-validated demand · page beats English on
 * something measurable). Evidence-led order when that bar is met: pt, then nl.
 */
// SAFETY: The surrounding typed contract establishes the asserted representation.
export const INDEXED_LOCALES: readonly Locale[] = ['en'] as const;

/**
 * Cookie carrying the locale a visitor was reading on the marketing site.
 *
 * Written by middleware on any `/xx` visit, read once on the first authenticated
 * dashboard render. It lives here rather than in middleware.ts because both the
 * Edge middleware and the Node server layout need it, and importing middleware.ts
 * from a layout would drag the whole Edge module into the server bundle.
 */
export const ARRIVAL_LOCALE_COOKIE = 'wisprs_locale';

export const ARRIVAL_LOCALE_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 30;

/** Right-to-left locales — drive `dir` on <html> and the RTL layout pass. */
// SAFETY: The surrounding typed contract establishes the asserted representation.
export const RTL_LOCALES: readonly Locale[] = ['ar'] as const;

/** Human-readable names, shown in the language switcher in the language itself. */
export const LOCALE_LABELS: Record<Locale, string> = {
  en: 'English',
  es: 'Español',
  fr: 'Français',
  de: 'Deutsch',
  pt: 'Português',
  it: 'Italiano',
  ja: '日本語',
  ko: '한국어',
  hi: 'हिन्दी',
  ar: 'العربية',
  ru: 'Русский',
  nl: 'Nederlands',
};

/** BCP-47 tags for `<html lang>` and OpenGraph `locale`. */
export const LOCALE_OG_TAGS: Record<Locale, string> = {
  en: 'en_US',
  es: 'es_ES',
  fr: 'fr_FR',
  de: 'de_DE',
  pt: 'pt_BR',
  it: 'it_IT',
  ja: 'ja_JP',
  ko: 'ko_KR',
  hi: 'hi_IN',
  ar: 'ar_AR',
  ru: 'ru_RU',
  nl: 'nl_NL',
};

export function isSupportedLocale(value: unknown): value is Locale {
  // SAFETY: The surrounding typed contract establishes the asserted representation.
  return isStringValue(value) && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

export function isIndexedLocale(value: unknown): value is Locale {
  return isSupportedLocale(value) && INDEXED_LOCALES.includes(value);
}

export function isRtlLocale(locale: Locale): boolean {
  return RTL_LOCALES.includes(locale);
}

export function localeDirection(locale: Locale): 'ltr' | 'rtl' {
  return isRtlLocale(locale) ? 'rtl' : 'ltr';
}

/**
 * Robots directives for a locale's pages. Non-indexed locales are `noindex,
 * follow` — hidden from search, but internal link equity still flows through to
 * the English originals (bible Ch. 2.1: no orphaned equity).
 */
export function localeRobots(locale: Locale): { index: boolean; follow: boolean } {
  return { index: isIndexedLocale(locale), follow: true };
}
